import React from "react";
import cx from "classnames";
import { compose } from "../../../../utils/fp";

import analytics, { events } from "../../../../utils/analytics";
import env from "../../../../utils/env";

import {
  formatPrice,
  formatQty,
  wrapWithParens,
  prependText,
  addCurrency
} from "../utils/helperFuncs";

import Grouping from "./grouping";

import "./Product.css";
import fallback from "./fallback.svg";
import crown from "./crown.svg";

const Product = props => {
  const {
    exploreDisplay,
    customization,
    customizeQuestionId,
    grouping,
    isSelect,
    name,
    salePrice,
    extraChoicePrice,
    productPageUrl,
    quantity,
    retailPrice,
    open,
    url,
    hasBeenAssigned,
    productLink,
    isSharedBox,
    isBoost,
  } = props;

  const titleStyle = cx({
    "lh-lg": true,
    "fs-16": true,
    "ff-ns": true,
    "pad-h-4": true,
    "case-caps": true,
    "fw-bold": true
  });

  const priceStyle = cx({
    price: true,
    "lh-md": true,
    "fw-normal": true,
    "ff-ns": true,
    "c-black-60": true,
    "pad-h-4": true,
    customize: customization || isBoost,
    "fs-16": true
  });

  const lockIconStyle = cx({
    fal: true,
    "c-black-60": true,
    "fa-lock-alt": !isSelect,
    "fa-lock-open-alt": isSelect,
    "fs-16": true
  });

  const itemQty = formatQty(quantity);
  const priceWithCurrency = compose(
    addCurrency,
    formatPrice
  );
  const formatAddonPrice = compose(
    wrapWithParens,
    prependText("+"),
    priceWithCurrency
  );
  const formatSalePriceWithQty = compose(
    prependText(itemQty),
    priceWithCurrency
  );

  //Display Containers
  const questionIdDisplay = () => (
    <div className="ff-ns fw-bold fs-16 lh-lg">
      {prependText("Customization ")(customizeQuestionId)}
    </div>
  );

  const canEditQuestion = () => {
    if(isSharedBox) {
      return false;
    }
    return (
      open &&
      (isBoost ||
      (customizeQuestionId &&
      (grouping !== Grouping.SELECT ||
        (grouping === Grouping.SELECT && isSelect))))
    );
  };

  const canViewItem = () => (
    isBoost ? !open : (open || hasBeenAssigned) && productPageUrl
  );

  const getEditUrl = () => (
    isBoost ?
      `${env.envURL}/boost` :
      `${env.envURL}/customize/?q=${customizeQuestionId}`
  );

  const getProductImg =() => (
    <div className="product-container">
      <img className="product-thumb" src={url || fallback} alt={name} />
      {/* boost crown badge might be used in future release */}
      {/* isBoost && <img className="boost-crown" src={crown} alt={name} /> */}
    </div>
  );

  const getAnalyticsViewItemTitle = () => (
    isBoost ?
      `Boost - ${name}` :
      `Add-Ons - ${name}`
  );

  const getAnalyticsEditItemTitle = () => (
    isBoost ?
      `Boost - ${name}` :
      `Customize - ${name}`
  );

  const renderPrice = () => (
    <div className={priceStyle}>
      {!salePrice && !!retailPrice && <span className="c-black-dark">{formatSalePriceWithQty(retailPrice)} </span>}
      {(!!salePrice) && (
        <span className="c-black-dark">
          {formatSalePriceWithQty(salePrice)}
        </span>
      )}
      {(salePrice === 0 || retailPrice === 0) && <span className="c-black-dark">{formatSalePriceWithQty(0)}</span>}
      {!isBoost && (!!retailPrice && !!salePrice) && (
        <span className="td-line-through retail-price">
          {priceWithCurrency(retailPrice)}
        </span>
      )}
    </div>
  );

  return (
    <div className="d-flex product pad-8">
      <div>
        {(open || hasBeenAssigned) && <a
          onClick={() => {
            analytics.track(events.CLICKED_ELEMENT, {
              element_name: "View Item",
              option_title: getAnalyticsViewItemTitle()
            });
          }}
          href={!hasBeenAssigned ? productPageUrl : `${env.originURL}/product/${productLink}`}
          className="pad-r-16 d-block"
        >
          {getProductImg()}
        </a>}
        {(!open && !hasBeenAssigned) && <img className="product-thumb" src={url || fallback} alt={name} />}
      </div>
      <div className="d-flex flex-aic pad-8">
        <div className="d-flex flex-fdc">
          {props.customization && !exploreDisplay && questionIdDisplay()}

          {/* Additional Choice price display */}
          {extraChoicePrice && !exploreDisplay && (
            <div className={priceStyle}>
              {formatAddonPrice(extraChoicePrice)}
            </div>
          )}

          {/* Product Title */}
          <div className={titleStyle}>{name}</div>

          {/* price display */}
          {renderPrice()}

          {/* Select Label Unlocked */}
          {grouping === Grouping.SELECT && (
            <div className="select-text d-flex fw-bold fs-16 ff-ns mar-t-8">
              <i className={lockIconStyle} />
              <span className="ff-ns">Select Only</span>
            </div>
          )}

          {/* Edit Item @TODO Connect to product page */}
          {canViewItem() && (
            <div className="pad-h-4 ff-ns">
              <a
                onClick={() => {
                  analytics.track(events.CLICKED_ELEMENT, {
                    element_name: "View Item",
                    option_title: getAnalyticsViewItemTitle()
                  });
                }}
                href={!hasBeenAssigned ? productPageUrl : `${env.originURL}/product/${productLink}`}
              >
                View Item
              </a>
            </div>
          )}

          {canEditQuestion() && (
            <div className="pad-h-4 ff-ns">
              <a
                onClick={() => {
                  analytics.track(events.CLICKED_ELEMENT, {
                    element_name: "Edit",
                    option_title: getAnalyticsEditItemTitle()
                  });
                }}
                href={getEditUrl()}
              >
                Edit
              </a>
            </div>
          )}

          {/* Upgrade to Unlock */}
          {customization && !exploreDisplay && !canEditQuestion() && open && !isSharedBox && (
            <div className="pad-h-4 ff-ns">
              <a
                onClick={() => {
                  analytics.track(events.CLICKED_ELEMENT, {
                    element_name: "Upgrade",
                    option_title: "Upgrade to Unlock - Customization Upgrade"
                  });
                }}
                href={`${env.envURL}/upgrade-confirmation`}
              >
                Upgrade to Unlock
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Product;



// WEBPACK FOOTER //
// ./src/Components/Panels/SharedComponents/Product/Product.js